import { useState } from "react";
import { Archive, ArchiveRestore, BookOpen, ExternalLink, Pencil, PlayCircle, Power, Trash2 } from "lucide-react";
import type { ResourceItem } from "../api/ResourcesApi";
import ConfirmModal from "shared/ConfirmModal";
import type { ButtonVariant } from "shared/Button";

type PendingAction = "delete" | "disable" | "archive" | null;

const CONFIRM_COPY: Record<Exclude<PendingAction, null>, { title: string; message: (title: string) => string; confirmLabel: string; variant: ButtonVariant }> = {
  delete: { title: "Delete resource", message: (t) => `Permanently delete "${t}"? This cannot be undone.`, confirmLabel: "Delete", variant: "danger" },
  disable: { title: "Disable resource", message: (t) => `Disable "${t}"? Employees will no longer see it.`, confirmLabel: "Disable", variant: "primary" },
  archive: { title: "Archive resource", message: (t) => `Archive "${t}"? It will be hidden from employees until restored.`, confirmLabel: "Archive", variant: "primary" },
};

interface ResourceCardProps {
  resource: ResourceItem;
  isAdmin: boolean;
  onEdit: (r: ResourceItem) => void;
  onToggleEnabled: (r: ResourceItem) => Promise<void>;
  onArchive: (r: ResourceItem) => Promise<void>;
  onRestore: (r: ResourceItem) => Promise<void>;
  onDelete: (r: ResourceItem) => Promise<void>;
}

export default function ResourceCard({
  resource, isAdmin, onEdit, onToggleEnabled, onArchive, onRestore, onDelete,
}: ResourceCardProps) {
  const [busy, setBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const isVideo = resource.resource_type === "video";
  const isArchived = !!resource.archived_at;

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Action failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  function handleToggleEnabled() {
    if (resource.is_enabled) {
      setPendingAction("disable");
    } else {
      run(() => onToggleEnabled(resource));
    }
  }

  async function handleConfirmedAction() {
    const action = pendingAction;
    if (action === "delete") await run(() => onDelete(resource));
    else if (action === "disable") await run(() => onToggleEnabled(resource));
    else if (action === "archive") await run(() => onArchive(resource));
    setPendingAction(null);
  }

  return (
    <div className={`bg-surface border rounded-2xl overflow-hidden flex flex-col ${isArchived ? "border-border opacity-60" : "border-border"}`}>
      {pendingAction && (
        <ConfirmModal
          title={CONFIRM_COPY[pendingAction].title}
          message={CONFIRM_COPY[pendingAction].message(resource.title)}
          confirmLabel={CONFIRM_COPY[pendingAction].confirmLabel}
          variant={CONFIRM_COPY[pendingAction].variant}
          confirming={busy}
          onConfirm={handleConfirmedAction}
          onCancel={() => setPendingAction(null)}
        />
      )}
      <div className={`px-5 pt-5 pb-4 border-b border-border`}>
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isVideo ? "bg-indigo-500/15" : "bg-amber-500/15"}`}>
            {isVideo
              ? <PlayCircle className="w-[18px] h-[18px] text-indigo-400" />
              : <BookOpen className="w-[18px] h-[18px] text-amber-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {resource.tag && (
                <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 font-inter border ${
                  isVideo
                    ? "text-indigo-300 bg-indigo-500/15 border-indigo-500/20"
                    : "text-amber-300 bg-amber-500/15 border-amber-500/20"
                }`}>
                  {resource.tag}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground font-inter uppercase tracking-wide">
                {isVideo ? "Video" : "Document"}
              </span>
              {isAdmin && !resource.is_enabled && !isArchived && (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted/50 border border-border/40 rounded-full px-2 py-0.5 font-inter">
                  Disabled
                </span>
              )}
              {isArchived && (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted/50 border border-border/40 rounded-full px-2 py-0.5 font-inter">
                  Archived
                </span>
              )}
            </div>
            <h2 className="text-sm font-semibold text-foreground font-inter">{resource.title}</h2>
            {resource.description && (
              <p className="text-xs text-muted-foreground font-inter mt-1 leading-relaxed">{resource.description}</p>
            )}
            {isAdmin && resource.role_names.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {resource.role_names.map((name) => (
                  <span key={name} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-muted-foreground border border-border font-inter">
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
            <button
              onClick={() => onEdit(resource)}
              disabled={busy}
              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleToggleEnabled}
              disabled={busy || isArchived}
              className={`p-1.5 hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50 ${resource.is_enabled ? "text-emerald-500 hover:text-emerald-400" : "text-muted-foreground hover:text-foreground"}`}
              title={resource.is_enabled ? "Disable" : "Enable"}
            >
              <Power className="w-3.5 h-3.5" />
            </button>
            {isArchived ? (
              <button
                onClick={() => run(() => onRestore(resource))}
                disabled={busy}
                className="p-1.5 text-muted-foreground hover:text-emerald-400 hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
                title="Restore"
              >
                <ArchiveRestore className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={() => setPendingAction("archive")}
                disabled={busy}
                className="p-1.5 text-muted-foreground hover:text-amber-400 hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
                title="Archive"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
            )}
            {isArchived && (
              <button
                onClick={() => setPendingAction("delete")}
                disabled={busy}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-surface-muted rounded-lg transition-colors disabled:opacity-50"
                title="Delete permanently"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {error && (
          <p className="text-[11px] text-destructive font-inter mt-2">{error}</p>
        )}
      </div>

      {isVideo ? (
        <div>
          <div className="relative w-full bg-slate-950" style={{ paddingTop: "56.25%" }}>
            <iframe
              src={resource.embed_url ?? resource.url}
              frameBorder={0}
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              title={resource.title}
            />
          </div>
          <div className="px-5 py-2 border-t border-border">
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary/80 font-inter"
            >
              <ExternalLink className="w-3 h-3" />
              Not loading? Open the video directly
            </a>
          </div>
        </div>
      ) : (
        <div className="p-5 pt-0">
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 hover:border-amber-500/40 text-amber-300 hover:text-amber-200 text-sm font-medium font-inter rounded-xl transition-all"
          >
            <ExternalLink className="w-4 h-4" />
            Open Document
          </a>
        </div>
      )}
    </div>
  );
}
